import React from 'react';
import { Box, useTheme } from '@mui/material';
import { AssetReadExternalExternalLink } from '@askporter/client-grieg-lyric';
import { HeaderExternalLinkButton } from '../../../../HeaderExternalLinkButton';
import { Typography } from '../../../../Typography';
import { DetailsPageHeaderTitle } from './component/DetailsPageHeaderTitle';

type DetailsPageHeaderProps = {
  t: (key: string, options?: Record<string, string | number>) => string;
  isSmallDevice: boolean;
  externalLink?: AssetReadExternalExternalLink;
  progressIndicator?: React.JSX.Element;
  title: string;
  subtitle?: React.ReactNode;
  icon: React.ReactNode;
  onChange?: (title?: string) => void;
  isTitleEditable?: boolean | undefined;
  chips?: React.ReactNode;
  /** Action(s) rendered on the same line as the icon/title, aligned to the right on larger devices. */
  actions?: React.ReactNode;
};

export const DetailsPageHeader: React.FC<DetailsPageHeaderProps> = ({
  isSmallDevice,
  externalLink,
  progressIndicator,
  isTitleEditable,
  title,
  onChange,
  subtitle,
  icon,
  chips,
  actions,
}) => {
  const {
    custom: { spacing },
  } = useTheme();

  return (
    <Box>
      <Box width="100%" display="flex" flexDirection="row" justifyContent={'flex-start'} data-testid="title-header">
        <Box display="flex" flexDirection="row" width={'100%'}>
          {icon && (
            <Box mr={4} data-testid="icon">
              {icon}
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: subtitle ? 'space-between' : 'center',
            }}
          >
            {/* Title - text with tooltip or text field */}
            <DetailsPageHeaderTitle
              title={title}
              isTitleEditable={!!isTitleEditable}
              onChange={(str: string) => onChange?.(str)}
              isSmallDevice={isSmallDevice}
            />
            {/* Subtitle */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {typeof subtitle === 'string' ? (
                <Typography component="p" color="textSecondary">
                  {subtitle}
                </Typography>
              ) : (
                subtitle
              )}
              <Box sx={{ pl: spacing?.md }}>{chips}</Box>
            </Box>
          </Box>
          {externalLink && !isSmallDevice ? <HeaderExternalLinkButton externalLink={externalLink} /> : null}
          {actions && !isSmallDevice ? (
            <Box ml={4} display="flex" alignItems="center">
              {actions}
            </Box>
          ) : null}
        </Box>
      </Box>
      {externalLink && isSmallDevice ? (
        <Box width="100%" mt={4}>
          <HeaderExternalLinkButton externalLink={externalLink} />
        </Box>
      ) : null}
      {actions && isSmallDevice ? (
        <Box width="100%" mt={4}>
          {actions}
        </Box>
      ) : null}
      {progressIndicator}
    </Box>
  );
};
